import {
  FormControl,
  Select,
  FormHelperText,
  SelectChangeEvent,
  MenuItem,
} from '@mui/material'
import { SxProps, useTheme } from '@mui/material/styles'
import { KFieldLabel } from 'components/core/KFieldLabel'
import { isEmpty } from 'lodash'
import {
  ForwardRefExoticComponent,
  ForwardedRef,
  RefAttributes,
  forwardRef,
} from 'react'

export interface KDropdownOption {
  label: string
  value: string
}

export interface KDropdownProps {
  label?: string
  labelSize?: 'small' | 'medium' | 'large'
  options: KDropdownOption[]
  value: string
  onChange: (value: string) => void
  sx?: SxProps
  size?: 'small' | 'medium'
  fullWidth?: boolean
  placeholder?: string
  disabled?: boolean
  error?: boolean
  helperText?: string
  required?: boolean
  ref?: ForwardedRef<HTMLDivElement>
}

export const KDropdown: ForwardRefExoticComponent<
  Omit<KDropdownProps, 'ref'> & RefAttributes<HTMLDivElement>
> = forwardRef((props, ref) => {
  const {
    label,
    labelSize,
    options,
    value,
    onChange,
    sx = {},
    size = 'medium',
    fullWidth = false,
    placeholder,
    disabled = false,
    error = false,
    helperText,
    required = false,
  } = props

  const theme = useTheme()

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value)
  }

  const getSelectHeight = () => {
    return size === 'small' ? 44 : 56
  }

  const getMobileSelectHeight = () => {
    return size === 'small' ? 40 : 52
  }

  return (
    <FormControl
      sx={{
        ...sx,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
      }}
      disabled={disabled}
      fullWidth={fullWidth}
      ref={ref}
      error={error}
    >
      {label && (
        <KFieldLabel
          labelSize={labelSize}
          sx={{
            marginBottom: 1,
            [theme.breakpoints.down('sm')]: {
              marginBottom: 0.5,
            },
          }}
          required={required}
        >
          {label}
        </KFieldLabel>
      )}
      <Select
        value={value || ''}
        onChange={handleChange}
        displayEmpty={!!placeholder}
        renderValue={(selected) => {
          if (!selected && placeholder) {
            return placeholder
          }
          const selectedOption = options.find(
            (option) => option.value === selected,
          )
          return selectedOption?.label || selected
        }}
        sx={{
          height: getSelectHeight(),
          borderRadius: 4,
          '&:hover': {
            backgroundColor: theme.palette.grey['A100'],
          },
          [theme.breakpoints.down('sm')]: {
            borderRadius: 3.5,
            height: getMobileSelectHeight(),
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: 2,
              mt: 0.5,
              boxShadow: theme.custom.shadows[0],
            },
          },
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {!isEmpty(helperText) && (
        <FormHelperText
          error={error}
          sx={{ fontSize: theme.typography.subtitle1.fontSize }}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  )
})
